import { useContext } from "react";
import { CellContext } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { VendorContext } from "@client-shared/providers/vendor";
import { EmptyValue } from "@bcc/components/ui/empty-value";
import { Popover, PopoverContent, PopoverTrigger } from "@bcc/components/ui/popover";
import { formatPrice } from "@bcc/lib/utils";
import { displayWholesaleForRegion, parseBagSizeToKg } from "@bcc/lib/products/cross-border-tax";
import { useTaxRatesContext } from "@bcc/contexts/tax-rates-context";
import { VariantWithProductData, type ShipToCountryData } from "../types";
import {
  setShipToCellTestId,
  setShipToTriggerTestId,
  setShipToPopoverContentTestId,
  setShipToCountryRowTestId,
  setShipToFlagTestId,
  homeRegionForVendor,
} from "../products-table.utils";
import { CountryFlag } from "./CountryFlag";
import { ShipToFlagIconDisplay } from "./ShipToFlagIconDisplay";

interface ShipToCountrySectionProps {
  rowIndex: number;
  country: ShipToCountryData;
  currencySymbol?: string;
  currencyIso?: string;
}

const ShipToCountrySection = ({ rowIndex, country, currencySymbol, currencyIso }: ShipToCountrySectionProps) => {
  const { t } = useTranslation();
  const countryName = t(`views.productsTable.shipTo.countries.${country.countryCode}`, {
    defaultValue: country.countryCode,
  });

  return (
    <div className="rounded-md border border-border p-3" data-testid={setShipToCountryRowTestId(rowIndex, country.countryCode)}>
      <div className="flex items-center gap-2">
        <CountryFlag countryCode={country.countryCode} />
        <span className="text-sm font-semibold">{t("views.productsTable.shipTo.heading", { country: countryName })}</span>
      </div>
      {/* min-w-0 lets each column shrink so long localized labels
          (e.g. "Groothandelsprijs") wrap instead of overflowing into the
          neighbouring column. */}
      <div className="mt-2 grid grid-cols-3 gap-3 text-xs">
        <div className="min-w-0">
          <p className="leading-tight break-words text-muted-foreground">{t("views.productsTable.shipTo.wholesalePrice")}</p>
          <p className="break-words font-medium">{formatPrice(country.wholesalePrice, currencySymbol, currencyIso)}</p>
        </div>
        <div className="min-w-0">
          <p className="leading-tight break-words text-muted-foreground">{t("views.productsTable.shipTo.retailPrice")}</p>
          <p className="break-words font-medium">{formatPrice(country.retailPrice, currencySymbol, currencyIso)}</p>
        </div>
        <div className="min-w-0">
          <p className="leading-tight break-words text-muted-foreground">{t("views.productsTable.shipTo.subscribers")}</p>
          <p className="break-words font-medium">{country.subscribers}</p>
        </div>
      </div>
    </div>
  );
};

export const ShipToCellMarkup = (props: CellContext<VariantWithProductData, unknown>) => {
  const { t } = useTranslation();
  const data = props.row.original;
  const [{ currencyObject, currentVendor }] = useContext(VendorContext);
  const ratesByRegion = useTaxRatesContext();
  const shipTo = data.shipTo ?? [];

  // Cross-border wholesale is stored KS-excluded (netto + VAT). Reconstruct the
  // coffee-tax-inclusive gross for the roaster's home-region row — the only row
  // that carried KS; other destinations never included it (BRG lodges KS), so
  // they render unchanged.
  const homeRegion = homeRegionForVendor(currentVendor?.countryCode, currentVendor?.region);
  const weightKg = parseBagSizeToKg(data.bagSize);
  const shipToDisplay = shipTo.map((country) =>
    country.countryCode === homeRegion
      ? { ...country, wholesalePrice: displayWholesaleForRegion(country.wholesalePrice, homeRegion, ratesByRegion, weightKg) }
      : country
  );

  if (shipTo.length === 0) {
    return (
      <span className="text-sm" data-testid={setShipToCellTestId(props.row.index)}>
        <EmptyValue />
      </span>
    );
  }

  return (
    <div data-testid={setShipToCellTestId(props.row.index)}>
      <Popover>
        <PopoverTrigger
          testId={setShipToTriggerTestId(props.row.index)}
          className="inline-flex cursor-pointer items-center rounded-full p-0.5 hover:bg-accent"
        >
          <ShipToFlagIconDisplay
            countryCodes={shipTo.map((country) => country.countryCode)}
            ariaLabel={t("views.productsTable.shipTo.ariaLabel", {
              countries: shipTo
                .map((country) => t(`views.productsTable.shipTo.countries.${country.countryCode}`, { defaultValue: country.countryCode }))
                .join(", "),
            })}
            getFlagTestId={(code) => setShipToFlagTestId(props.row.index, code)}
          />
        </PopoverTrigger>
        <PopoverContent testId={setShipToPopoverContentTestId(props.row.index)} align="start" className="w-80 space-y-2">
          <p className="text-sm font-semibold">{data.productTitle}</p>
          {shipToDisplay.map((country) => (
            <ShipToCountrySection
              key={country.countryCode}
              rowIndex={props.row.index}
              country={country}
              currencySymbol={currencyObject?.symbol}
              currencyIso={currencyObject?.iso}
            />
          ))}
          <p className="pt-1 text-[10px] italic text-muted-foreground">{t("views.productsTable.shipTo.taxNote")}</p>
        </PopoverContent>
      </Popover>
    </div>
  );
};
